/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from 'react'
import { Button } from 'react-bootstrap'

import i18n from 'live/services/i18n'

import { IAlarp, IAlarpThreshold, IBarrierStatusSpecialStatus } from 'plugin-codaf-risk-ext-bowtie/types'
import {
    IResourceBarrierPFD,
    IResourceBarrierStatus,
    IResourceThreatStatus,
    IEscalationFactorQualitativeBarriers,
    IMandatoryBarriers,
    IManualInteractionBarrierStatus,
    IRiskAcceptance
} from 'plugin-codaf-risk-ext-bowtie/types/reports'

import {
    RiskAcceptance,
    ResourceThreatStatus,
    ResourceBarrierStatus,
    ResourceBarrierPFDs,
    ManualInteractionBarriersStatus,
    MandatoryBarriers,
    EscalationFactorQualitativeBarriers,
    ReportPagination,
    ResourceBarrierAnalysis,
    ResourceConsequenceStatus
} from './components'
import { ViewContainer } from './styles'

import { Scrollbars } from 'plugin-generic-components/shared/components/components'

interface IProps {
    preset: string
    info?:
        | IRiskAcceptance
        | IResourceThreatStatus
        | IResourceBarrierStatus
        | IResourceBarrierPFD
        | IManualInteractionBarrierStatus
        | IMandatoryBarriers
        | IEscalationFactorQualitativeBarriers
        | null
    time: string | null
    startDay: string | null
    endDay: string | null
    startHour: string | null
    endHour: string | null
    alarpList: IAlarp[]
    dictionaryStatusMap: Map<number, IBarrierStatusSpecialStatus>
}

interface ISelect {
    value: number
    label: string
}

export const View: React.FC<IProps> = ({
    dictionaryStatusMap,
    alarpList,
    preset,
    time,
    startDay,
    startHour,
    endDay,
    endHour
}) => {
    const [limit, setLimit] = useState<ISelect>({ value: 30, label: `30 ${i18n('items')} | ${i18n('page')}` })
    const [offset, setOffset] = useState(0)
    const [itemsLength, setItemsLength] = useState<number>(0)
    const [page, setPage] = useState<number>(1)

    const [update] = useState<number>(0)

    const riskAcceptanceRef = useRef(null)
    const resourceThreatStatusRef = useRef(null)
    const resourceConsequenceStatusRef = useRef(null)
    const resourceBarrierStatusRef = useRef(null)
    const resourceBarrierPFDRef = useRef(null)
    const manualInteractionBarriersStatusRef = useRef(null)
    const mandatoryBarriersRef = useRef(null)
    const resourceBarrierAnalysisRef = useRef(null)
    const [exportLoading, setExportLoading] = useState<boolean>(false)

    const generateExportButton = (): JSX.Element => {
        return (
            <Button
                className="export-button"
                onClick={() => {
                    riskAcceptanceRef.current.generateRiskAcceptanceReport()
                }}
            >
                <i className="fas fa-download" /> Export data
            </Button>
        )
    }

    useEffect(() => {
        setOffset(0)
    }, [preset, time, startDay, startHour, endDay, endHour, update])

    switch (preset) {
        case i18n('Risk Acceptance / Risk Level'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                riskAcceptanceRef.current.exportRiskAcceptanceAndRiskLevel()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <RiskAcceptance
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        alarpList={alarpList}
                        page={page}
                        setPage={setPage}
                        ref={riskAcceptanceRef}
                        setExportLoading={setExportLoading}
                    />
                </ViewContainer>
            )
            break
        case i18n('Resource Threat Status'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                resourceThreatStatusRef.current.exportResourceThreatStatus()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <ResourceThreatStatus
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        page={page}
                        setPage={setPage}
                        ref={resourceThreatStatusRef}
                        setExportLoading={setExportLoading}
                    />
                </ViewContainer>
            )
            break
        case i18n('Resource Consequence Status'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                resourceConsequenceStatusRef.current.exportResourceConsequenceStatus()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <ResourceConsequenceStatus
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        page={page}
                        setPage={setPage}
                        ref={resourceConsequenceStatusRef}
                        setExportLoading={setExportLoading}
                    />
                </ViewContainer>
            )
            break
        case i18n('Resource Barrier Status'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                resourceBarrierStatusRef.current.exportResourceBarrierStatus()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <ResourceBarrierStatus
                        dictionaryStatusMap={dictionaryStatusMap}
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        page={page}
                        setPage={setPage}
                        ref={resourceBarrierStatusRef}
                        setExportLoading={setExportLoading}
                    />
                </ViewContainer>
            )
            break
        case i18n('Resource Barrier PFD'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                resourceBarrierPFDRef.current.exportResourceBarrierPFD()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <ResourceBarrierPFDs
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        page={page}
                        setPage={setPage}
                        dictionaryStatusMap={dictionaryStatusMap}
                        ref={resourceBarrierPFDRef}
                        setExportLoading={setExportLoading}
                    />
                </ViewContainer>
            )
            break
        case i18n('Manual Interaction Barriers Status'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                manualInteractionBarriersStatusRef.current.exportManualInteractionBarrierStatus()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <ManualInteractionBarriersStatus
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        page={page}
                        setPage={setPage}
                        dictionaryStatusMap={dictionaryStatusMap}
                        ref={manualInteractionBarriersStatusRef}
                        setExportLoading={setExportLoading}
                    />
                </ViewContainer>
            )
            break
        case i18n('Current Mandatory Barriers'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                mandatoryBarriersRef.current.exportCurrentMandatoryBarrier()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <MandatoryBarriers
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        ref={mandatoryBarriersRef}
                        setExportLoading={setExportLoading}
                        page={page}
                        setPage={setPage}
                    />
                </ViewContainer>
            )
            break
        case i18n('Escalation Factor Qualitative Barriers'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        {generateExportButton()}
                    </div>
                    <EscalationFactorQualitativeBarriers />
                </ViewContainer>
            )
            break
        case i18n('Resource Barrier Analysis'):
            return (
                <ViewContainer>
                    <div className="titleBar">
                        <span>
                            {i18n('Report')} | {preset}
                        </span>
                    </div>
                    <div className="pagination">
                        <ReportPagination
                            total={itemsLength}
                            offset={offset}
                            setOffset={setOffset}
                            limit={limit}
                            setLimit={setLimit}
                            setPage={setPage}
                        />
                        <Button
                            className="export-button"
                            onClick={() => {
                                resourceBarrierAnalysisRef.current.exportResourceBarrierAnalysis()
                            }}
                        >
                            {exportLoading ? (
                                <i className="fa fa-spinner fa-spin fa-x2" />
                            ) : (
                                <>
                                    <i className="fas fa-download" /> Export data
                                </>
                            )}
                        </Button>
                    </div>
                    <ResourceBarrierAnalysis
                        time={time}
                        setItemsLength={setItemsLength}
                        start={offset}
                        numberOfItems={limit.value}
                        startDay={startDay}
                        endDay={endDay}
                        startHour={startHour}
                        endHour={endHour}
                        page={page}
                        setPage={setPage}
                        dictionaryStatusMap={dictionaryStatusMap}
                        ref={resourceBarrierAnalysisRef}
                        setExportLoading={setExportLoading}
                    />
                </ViewContainer>
            )
            break
        default:
            return (
                <ViewContainer>
                    <span>{i18n('Select a preset and a time range to view a report')}</span>
                </ViewContainer>
            )
    }
}
